import { useTheme, type Breakpoint, styled } from "@schaeffler-commerce/design-system";
import { RouterLink } from "~/common/components/Link";
import type ComponentTypes from "~/features/cms/componentTypes";
import classNames from "classnames";
import { isSiteRelativeUrl, resolveUrl } from "~/utils/url/urlResolver";

export interface SimpleResponsiveBannerMediaDTO {
  code?: string;
  format?: string;
  url?: string;
  altText?: string;
}

export interface CMSSimpleResponsiveBannerProps {
  type: ComponentTypes.CMSSimpleResponsiveBanner;
  id: string;
  className?: string;
  imageLink?: string;
  urlLink?: string;
  media?: { qualifier?: string; name?: string };
  medias?: SimpleResponsiveBannerMediaDTO[];
}

type BreakpointName = Exclude<Breakpoint, "desktop-l">;

const FORMAT_BREAKPOINT: Record<string, BreakpointName> = {
  mobile: "phone",
  tablet: "tablet",
  desktop: "desktop-s",
  widescreen: "desktop-m",
};

export const CMSSimpleResponsiveBanner = ({
  urlLink,
  imageLink,
  medias,
  className,
}: CMSSimpleResponsiveBannerProps) => {
  const theme = useTheme();

  const resolved = (medias ?? [])
    .map((m) => ({
      url: m.url,
      altText: m.altText,
      value: FORMAT_BREAKPOINT[m.format ?? ""]
        ? theme.breakpoints.values[FORMAT_BREAKPOINT[m.format ?? ""]]
        : undefined,
    }))
    .filter((m): m is typeof m & { value: number } => m.value !== undefined && Boolean(m.url))
    .sort((a, b) => b.value - a.value);

  if (resolved.length === 0) {
    return null;
  }

  const sources = resolved.slice(0, -1);
  const fallback = resolved[resolved.length - 1];
  const altText = resolved.find((m) => m.altText)?.altText ?? "";

  const linkHref = urlLink ?? imageLink;

  const picture = (
    <ResponsivePicture>
      {sources.map((m) => (
        <source key={m.value} media={`(min-width:${m.value}px)`} srcSet={resolveUrl(m.url!)} />
      ))}
      <ResponsiveImage src={resolveUrl(fallback.url!)} alt={altText} />
    </ResponsivePicture>
  );

  if (!linkHref) {
    return <Root className={classNames("scha__cms", className)}>{picture}</Root>;
  }

  // Site-relative CMS urls navigate through the router; everything else stays a raw anchor.
  return (
    <Root className={classNames("scha__cms", className)}>
      {isSiteRelativeUrl(linkHref) ? (
        <BannerLink to={linkHref}>{picture}</BannerLink>
      ) : (
        <BannerLink as="a" href={resolveUrl(linkHref)}>
          {picture}
        </BannerLink>
      )}
    </Root>
  );
};

const Root = styled("div")({ display: "block", width: "100%" });

const ResponsivePicture = styled("picture")({ display: "block", width: "100%" });

const ResponsiveImage = styled("img")({
  display: "block",
  width: "100%",
  height: "auto",
  maxWidth: "100%",
});

const BannerLink = styled(RouterLink)({ display: "block", width: "100%" });
